import React, { useEffect } from "react";
import { motion } from "framer-motion";
import { Link, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import {
  ArrowLeft,
  Calendar,
  Clock,
  User,
  Share2,
  BookOpen,
  ChevronRight,
  ArrowRight,
} from "lucide-react";

import { BlogsData } from './BlogsData';

const SingleBlogPost = () => {
  const { productId } = useParams();
  const thisProduct = BlogsData.find((prod) => prod.ID === parseInt(productId));

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [productId]);

  const handleShare = async () => {
    const shareUrl = typeof window !== "undefined" ? window.location.href : "";
    const shareData = {
      title: thisProduct?.title || "Terces Solutions Blog",
      text: thisProduct?.title || "Check out this article from Terces Solutions",
      url: shareUrl,
    };
    try {
      if (navigator.share) {
        await navigator.share(shareData);
        return;
      }
      if (navigator.clipboard) {
        await navigator.clipboard.writeText(shareUrl);
        toast.success("Article link copied to clipboard!");
        return;
      }
      window.prompt("Copy this link to share:", shareUrl);
    } catch (err) {
      if (err && err.name === "AbortError") return;
      toast.error("Unable to share right now. Please copy the link manually.");
    }
  };

  if (!thisProduct) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F6F4EF]">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Blog Not Found
          </h1>
          <Link to="/Blogs" className="text-primary hover:underline">
            Return to Blogs
          </Link>
        </div>
      </div>
    );
  }

  const remainingBlogs = BlogsData.filter((blog) => blog.ID !== thisProduct.ID);

  return (
    <div className="min-h-screen bg-[#F6F4EF]">
      {/* Hero Section with Featured Image */}
      <motion.section
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6 }}
        className="relative w-full h-[60vh] min-h-[500px] max-h-[700px] overflow-hidden"
      >
        {/* Background Image */}
        <div className="absolute inset-0">
          <motion.img
            initial={{ scale: 1.1 }}
            animate={{ scale: 1 }}
            transition={{ duration: 1.5, ease: [0.22, 1, 0.36, 1] }}
            src={thisProduct.image}
            alt={thisProduct.title}
            className="w-full h-full object-cover"
          />
          {/* Dark Overlay */}
          <div className="absolute inset-0 bg-gradient-to-b from-gray-900/70 via-gray-900/50 to-[#F6F4EF]" />
        </div>

        {/* Content */}
        <div className="relative z-10 h-full flex items-end">
          <div className=" mx-auto px-6 md:px-16 pb-16 md:pb-20 w-full">
            {/* Back Button */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="mb-8"
            >
              <Link
                to="/Blogs"
                className="inline-flex items-center gap-2 text-white/90 hover:text-white transition-colors group"
              >
                <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
                <span className="text-sm font-semibold">Back to Blogs</span>
              </Link>
            </motion.div>

            {/* Category Badge */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="mb-4"
            >
              <span className="inline-block px-4 py-2 bg-primary text-white text-xs font-bold uppercase tracking-wider rounded-full">
                {thisProduct.category}
              </span>
            </motion.div>

            {/* Title */}
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-extrabold text-white leading-[1.1] mb-6"
              style={{ letterSpacing: "-0.02em" }}
            >
              {thisProduct.title}
            </motion.h1>

            {/* Meta Information */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.5 }}
              className="flex flex-wrap items-center gap-6 text-white/90"
            >
              <div className="flex items-center gap-2">
                <User className="w-4 h-4" />
                <span className="text-sm font-medium">
                  {thisProduct.author}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                <span className="text-sm">{thisProduct.date}</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                <span className="text-sm">{thisProduct.readTime}</span>
              </div>
            </motion.div>
          </div>
        </div>
      </motion.section>

      {/* Main Content */}
      <section className="relative py-16 md:py-20">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 xl:gap-20">
            {/* Main Article Content */}
            <motion.article
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="lg:col-span-8 xl:col-span-9"
            >
              <div className="bg-white rounded-3xl p-10 md:p-14 lg:p-16 xl:p-20 shadow-lg">
                {/* Article Body */}
                <div className="prose prose-xl max-w-none">
                  {/* Body 1 — no drop-cap, plain lead paragraph */}
                  {thisProduct.body1 && (
                    <motion.p
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.3 }}
                      className="text-gray-600 text-xl md:text-2xl leading-relaxed mb-10 border-l-4 border-primary pl-5 italic"
                    >
                      {thisProduct.body1}
                    </motion.p>
                  )}

                  {/* Body Head 1 */}
                  {thisProduct.bodyhead1 && (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.4 }}
                      className="mt-12 mb-6"
                    >
                      <h2
                        className="text-2xl md:text-3xl font-extrabold text-gray-900 mb-3"
                        style={{ letterSpacing: "-0.02em" }}
                      >
                        {thisProduct.bodyhead1}
                      </h2>
                      <motion.div
                        className="w-12 h-1 bg-primary rounded-full"
                        initial={{ scaleX: 0 }}
                        animate={{ scaleX: 1 }}
                        transition={{ duration: 0.6, delay: 0.5 }}
                        style={{ transformOrigin: "left" }}
                      />
                    </motion.div>
                  )}

                  {/* Body 2 */}
                  {thisProduct.body2 && (
                    <p className="text-gray-700 text-lg leading-relaxed mb-8">
                      {thisProduct.body2}
                    </p>
                  )}

                  {/* Inline image after body2 */}
                  {thisProduct.image2 && (
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.5 }}
                      className="my-10 rounded-2xl overflow-hidden shadow-md"
                    >
                      <img
                        src={thisProduct.image2}
                        alt={thisProduct.title}
                        className="w-full h-64 md:h-80 object-cover"
                      />
                    </motion.div>
                  )}

                  {/* Body Head 2 */}
                  {thisProduct.bodyhead2 && (
                    <div className="mt-12 mb-6">
                      <h2
                        className="text-2xl md:text-3xl font-extrabold text-gray-900 mb-3"
                        style={{ letterSpacing: "-0.02em" }}
                      >
                        {thisProduct.bodyhead2}
                      </h2>
                      <div className="w-12 h-1 bg-primary rounded-full" />
                    </div>
                  )}

                  {/* Body 3 */}
                  {thisProduct.body3 && (
                    <p className="text-gray-700 text-lg leading-relaxed mb-8">
                      {thisProduct.body3}
                    </p>
                  )}

                  {/* Body Head 3 */}
                  {thisProduct.bodyhead3 && (
                    <div className="mt-12 mb-6">
                      <h2
                        className="text-2xl md:text-3xl font-extrabold text-gray-900 mb-3"
                        style={{ letterSpacing: "-0.02em" }}
                      >
                        {thisProduct.bodyhead3}
                      </h2>
                      <div className="w-32 h-1 bg-primary rounded-full" />
                    </div>
                  )}

                  {/* Body 4 */}
                  {thisProduct.body4 && (
                    <p className="text-gray-700 text-lg leading-relaxed mb-8">
                      {thisProduct.body4}
                    </p>
                  )}

                  {/* Body Head 4 */}
                  {thisProduct.bodyhead4 && (
                    <div className="mt-12 mb-6">
                      <h2
                        className="text-2xl md:text-3xl font-extrabold text-gray-900 mb-3"
                        style={{ letterSpacing: "-0.02em" }}
                      >
                        {thisProduct.bodyhead4}
                      </h2>
                      <div className="w-32 h-1 bg-primary rounded-full" />
                    </div>
                  )}

                  {/* Body 5 */}
                  {thisProduct.body5 && (
                    <p className="text-gray-700 text-lg leading-relaxed mb-8">
                      {thisProduct.body5}
                    </p>
                  )}

                  {/* Body Head 5 */}
                  {thisProduct.bodyhead5 && (
                    <div className="mt-12 mb-6">
                      <h2
                        className="text-2xl md:text-3xl font-extrabold text-gray-900 mb-3"
                        style={{ letterSpacing: "-0.02em" }}
                      >
                        {thisProduct.bodyhead5}
                      </h2>
                      <div className="w-32 h-1 bg-primary rounded-full" />
                    </div>
                  )}

                  {/* Body 6 */}
                  {thisProduct.body6 && (
                    <p className="text-gray-700 text-lg leading-relaxed mb-8">
                      {thisProduct.body6}
                    </p>
                  )}

                  {/* Continue pattern for body7-12 */}
                  {thisProduct.bodyhead6 && (
                    <div className="mt-12 mb-6">
                      <h2
                        className="text-2xl md:text-3xl font-extrabold text-gray-900 mb-3"
                        style={{ letterSpacing: "-0.02em" }}
                      >
                        {thisProduct.bodyhead6}
                      </h2>
                      <div className="w-32 h-1 bg-primary rounded-full" />
                    </div>
                  )}

                  {thisProduct.body7 && (
                    <p className="text-gray-700 text-lg leading-relaxed mb-8">
                      {thisProduct.body7}
                    </p>
                  )}

                  {thisProduct.bodyhead7 && (
                    <div className="mt-12 mb-6">
                      <h2
                        className="text-2xl md:text-3xl font-extrabold text-gray-900 mb-3"
                        style={{ letterSpacing: "-0.02em" }}
                      >
                        {thisProduct.bodyhead7}
                      </h2>
                      <div className="w-32 h-1 bg-primary rounded-full" />
                    </div>
                  )}

                  {thisProduct.body8 && (
                    <p className="text-gray-700 text-lg leading-relaxed mb-8">
                      {thisProduct.body8}
                    </p>
                  )}

                  {thisProduct.bodyhead8 && (
                    <div className="mt-12 mb-6">
                      <h2
                        className="text-2xl md:text-3xl font-extrabold text-gray-900 mb-3"
                        style={{ letterSpacing: "-0.02em" }}
                      >
                        {thisProduct.bodyhead8}
                      </h2>
                      <div className="w-32 h-1 bg-primary rounded-full" />
                    </div>
                  )}

                  {thisProduct.body9 && (
                    <p className="text-gray-700 text-lg leading-relaxed mb-8">
                      {thisProduct.body9}
                    </p>
                  )}

                  {thisProduct.bodyhead9 && (
                    <div className="mt-12 mb-6">
                      <h2
                        className="text-2xl md:text-3xl font-extrabold text-gray-900 mb-3"
                        style={{ letterSpacing: "-0.02em" }}
                      >
                        {thisProduct.bodyhead9}
                      </h2>
                      <div className="w-32 h-1 bg-primary rounded-full" />
                    </div>
                  )}

                  {thisProduct.body10 && (
                    <p className="text-gray-700 text-lg leading-relaxed mb-8">
                      {thisProduct.body10}
                    </p>
                  )}

                  {thisProduct.bodyhead10 && (
                    <div className="mt-12 mb-6">
                      <h2
                        className="text-2xl md:text-3xl font-extrabold text-gray-900 mb-3"
                        style={{ letterSpacing: "-0.02em" }}
                      >
                        {thisProduct.bodyhead10}
                      </h2>
                      <div className="w-32 h-1 bg-primary rounded-full" />
                    </div>
                  )}

                  {thisProduct.body11 && (
                    <p className="text-gray-700 text-lg leading-relaxed mb-8">
                      {thisProduct.body11}
                    </p>
                  )}

                  {thisProduct.bodyhead11 && (
                    <div className="mt-12 mb-6">
                      <h2
                        className="text-2xl md:text-3xl font-extrabold text-gray-900 mb-3"
                        style={{ letterSpacing: "-0.02em" }}
                      >
                        {thisProduct.bodyhead11}
                      </h2>
                      <div className="w-32 h-1 bg-primary rounded-full" />
                    </div>
                  )}

                  {thisProduct.body12 && (
                    <p className="text-gray-700 text-lg leading-relaxed mb-8">
                      {thisProduct.body12}
                    </p>
                  )}

                  {thisProduct.bodyhead12 && (
                    <div className="mt-12 mb-6">
                      <h2
                        className="text-2xl md:text-3xl font-extrabold text-gray-900 mb-3"
                        style={{ letterSpacing: "-0.02em" }}
                      >
                        {thisProduct.bodyhead12}
                      </h2>
                      <div className="w-32 h-1 bg-primary rounded-full" />
                    </div>
                  )}
                </div>

                {/* Share Section */}
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.6 }}
                  className="mt-16 pt-10 border-t-2 border-gray-200"
                >
                  <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
                    <div>
                      <h4 className="text-2xl font-bold text-gray-900 mb-2">
                        Enjoyed this article?
                      </h4>
                      <p className="text-gray-600">
                        Share it with your network
                      </p>
                    </div>
                    <button
                      onClick={handleShare}
                      className="flex items-center gap-3 px-8 py-4 bg-primary hover:bg-primary-dark text-white rounded-full font-semibold text-lg shadow-lg hover:shadow-xl transition-all duration-300 group"
                    >
                      <Share2 className="w-5 h-5 group-hover:rotate-12 transition-transform" />
                      <span>Share Article</span>
                    </button>
                  </div>
                </motion.div>
              </div>
            </motion.article>

            {/* Sidebar - Related Blogs */}
            <motion.aside
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="lg:col-span-4 xl:col-span-3"
            >
              <div className="sticky top-24 space-y-8">
                {/* Related Articles Card */}
                <div className="bg-white rounded-3xl p-8 shadow-xl text-black">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="p-2.5 bg-white/20 backdrop-blur-sm rounded-xl">
                      <BookOpen className="w-5 h-5 text-black" />
                    </div>
                    <h3 className="text-2xl font-extrabold">
                      Related Articles
                    </h3>
                  </div>
                  <p className="text-black/80 text-sm leading-relaxed">
                    Explore more insights from our expert team on restaurant
                    consulting and business growth.
                  </p>
                </div>

                {/* Articles List */}
                <div className="space-y-4">
                  {remainingBlogs.slice(0, 3).map((blog, index) => (
                    <Link
                      key={blog.ID}
                      to={`/Blogs/${blog.ID}`}
                      className="group block"
                    >
                      <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1 * index, duration: 0.5 }}
                        whileHover={{ y: -4 }}
                        className="bg-white rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 border border-gray-100"
                      >
                        {/* Image */}
                        <div className="relative h-40 overflow-hidden">
                          <img
                            src={blog.image}
                            alt={blog.title}
                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                          />
                          {/* Category Badge */}
                          <div className="absolute top-3 left-3">
                            <span className="px-3 py-1.5 bg-white/95 backdrop-blur-sm text-primary text-xs font-bold uppercase tracking-wider rounded-full shadow-lg">
                              {blog.category}
                            </span>
                          </div>
                          {/* Gradient Overlay */}
                          <div className="absolute inset-0 bg-gradient-to-t from-gray-900/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                        </div>

                        {/* Content */}
                        <div className="p-5">
                          <h4 className="text-base font-bold text-gray-900 group-hover:text-primary transition-colors line-clamp-2 mb-3 leading-snug">
                            {blog.title}
                          </h4>

                          <div className="flex items-center justify-between text-xs text-gray-500">
                            <div className="flex items-center gap-1.5">
                              <Calendar className="w-3.5 h-3.5" />
                              <span>{blog.date}</span>
                            </div>
                            <div className="flex items-center gap-1.5">
                              <Clock className="w-3.5 h-3.5" />
                              <span>{blog.readTime}</span>
                            </div>
                          </div>

                          {/* Read More Indicator */}
                          <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between">
                            <span className="text-xs font-semibold text-gray-600 group-hover:text-primary transition-colors">
                              Read Article
                            </span>
                            <ArrowRight className="w-4 h-4 text-gray-400 group-hover:text-primary group-hover:translate-x-1 transition-all" />
                          </div>
                        </div>
                      </motion.div>
                    </Link>
                  ))}
                </div>

                {/* View All CTA */}
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.6 }}
                  whileHover={{ scale: 1.02 }}
                  className="relative"
                >
                  <Link to="/Blogs" className="block">
                    <div className="relative overflow-hidden bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 group">
                      {/* Shine Effect */}
                      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700" />

                      <div className="relative flex items-center justify-between text-black">
                        <div>
                          <div className="text-lg font-bold text-black mb-1">
                            View All Articles
                          </div>
                          <div className="text-xs text-black/80">
                            Discover more insights
                          </div>
                        </div>
                        <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center group-hover:bg-white/30 transition-colors">
                          <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                        </div>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              </div>
            </motion.aside>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SingleBlogPost;
